"use client";

import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { FiInfo, FiVolume2 } from "react-icons/fi";
import { LuImage } from "react-icons/lu";
import { MdDeleteOutline } from "react-icons/md";
import ConfirmPopUp from "../../popup/confirmPopUp";
import StatusPopUp from "../../popup/statusPopUp";
import anagramAPI from "../../../services/anagram/anagramAPI";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";

type AnagramItem = {
    id: number;
    no: number;
    word: string;
    swapWord: string[];
    imageWordFileName: string;
    imageWordFile: File | null;
    imageWordUrl: string | null;
    audioWordFileName: string;
    audioWordFile: File | null;
    audioWordUrl: string | null;
    imageSwapFileName: string;
    imageSwapFile: File | null;
    imageSwapUrl: string | null;
    audioSwapFileName: string;
    audioSwapFile: File | null;
    audioSwapUrl: string | null;
    hasHint: boolean;
    hintText: string;
    hintImageFileName: string;
    hintImageFile: File | null;
    hintImageUrl: string | null;
    hintAudioFileName: string;
    hintAudioFile: File | null;
    hintAudioUrl: string | null;
};

type UploadTarget = {
    key: string;
    file: File;
    fileName: string;
    sectionName: string;
    refId: string;
};

type GameSkills = {
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
};

type GameSkillKey = keyof GameSkills;

const DEFAULT_GAME_SKILLS: GameSkills = {
    skill_listening_speaking: false,
    skill_lang_nature: false,
    skill_reading: false,
    skill_writing: false,
};

type FindTheAnagramProps = {
    createdGameData?: {
        id: number | string;
        exercise_name?: string;
        uuid?: string;
        description?: string | null;
    } | null;
    initialGameDetail?: {
        id_game_info?: number | string;
        game_info?: {
            id?: number | string;
            uuid?: string;
            exercise_name?: string;
            suggestion?: string | null;
            description?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
        };
        questions?: Array<{
            id?: number;
            no?: number;
            word?: string;
            question?: string;
            swap_word?: string[] | string;
            answer?: string;
            hint?: string | null;
            image_word?: string | null;
            sound_word?: string | null;
            image_swap?: string | null;
            sound_swap?: string | null;
            image_swap_word?: string | null;
            sound_swap_word?: string | null;
            image_hint?: string | null;
            sound_hint?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
        }>;
        anagrams?: Array<{
            id?: number;
            no?: number;
            word?: string;
            question?: string;
            swap_word?: string[] | string;
            answer?: string;
            hint?: string | null;
            image_word?: string | null;
            sound_word?: string | null;
            image_swap?: string | null;
            sound_swap?: string | null;
            image_swap_word?: string | null;
            sound_swap_word?: string | null;
            image_hint?: string | null;
            sound_hint?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
        }>;
    } | null;
    onCompleted?: () => void;
};

function getFileExtension(fileName: string) {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
        return "";
    }
    return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
    if (!path) {
        return "";
    }

    const cleanPath = String(path);
    const bySlash = cleanPath.split("/");
    const candidate = bySlash[bySlash.length - 1] || "";
    return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
    if (!path) {
        return null;
    }

    const raw = String(path).trim();
    if (!raw) {
        return null;
    }

    if (raw.startsWith("http://") || raw.startsWith("https://")) {
        return raw;
    }

    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://thaipilacreate.gforcesolution.com";
    const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
    return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

const thaiSegmenter = typeof Intl !== "undefined" && "Segmenter" in Intl
    ? new Intl.Segmenter("th-TH", { granularity: "grapheme" })
    : null;

function buildSwapWord(value: string): string[] {
    const raw = String(value ?? "").trim();
    if (!raw) return [];

    if (thaiSegmenter) {
        return Array.from(thaiSegmenter.segment(raw))
            .map(s => s.segment)
            .filter(s => s.trim() !== "");
    }

    return Array.from(raw).filter(s => s.trim() !== "");
}

function buildWord(value: string): string {
    return String(value ?? "").replace(/\s+/g, "").trim();
}

function normalizeSwapWord(value: unknown, fallbackWord: string): string[] {
    if (Array.isArray(value)) {
        const normalizedEntries = value
            .map((entry) => String(entry))
            .filter((entry) => entry.trim() !== "");

        const joined = normalizedEntries.join("").trim();
        if (joined.startsWith("[") || joined.startsWith("\"[")) {
            return normalizeSwapWord(joined, fallbackWord);
        }

        return normalizedEntries;
    }

    if (typeof value === "string") {
        let current = value.trim();

        for (let attempt = 0; attempt < 2; attempt += 1) {
            const looksLikeJsonArrayString = current.startsWith("[") || current.startsWith("\"[");
            if (!looksLikeJsonArrayString) {
                break;
            }

            try {
                const parsed = JSON.parse(current) as unknown;
                if (Array.isArray(parsed)) {
                    return parsed
                        .map((entry) => String(entry))
                        .filter((entry) => entry.trim() !== "");
                }
                if (typeof parsed === "string") {
                    current = parsed.trim();
                    continue;
                }
                break;
            } catch {
                break;
            }
        }
    }

    return buildSwapWord(buildWord(fallbackWord));
}

function normalizeSkillBoolean(value: unknown): boolean | null {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") {
            return true;
        }
        if (normalized === "false") {
            return false;
        }
    }

    return null;
}

function normalizeGameSkillsOrNull(value: Partial<Record<GameSkillKey, unknown>>) {
    const listening = normalizeSkillBoolean(value.skill_listening_speaking);
    const langNature = normalizeSkillBoolean(value.skill_lang_nature);
    const reading = normalizeSkillBoolean(value.skill_reading);
    const writing = normalizeSkillBoolean(value.skill_writing);

    if (listening === null || langNature === null || reading === null || writing === null) {
        return null;
    }

    return {
        skill_listening_speaking: listening,
        skill_lang_nature: langNature,
        skill_reading: reading,
        skill_writing: writing,
    };
}

function areGameSkillsEqual(left: GameSkills, right: GameSkills) {
    return (
        left.skill_listening_speaking === right.skill_listening_speaking &&
        left.skill_lang_nature === right.skill_lang_nature &&
        left.skill_reading === right.skill_reading &&
        left.skill_writing === right.skill_writing
    );
}

function extractGameSkillsFromUnknown(value: Partial<Record<GameSkillKey, unknown>>): GameSkills {
    const normalized = normalizeGameSkillsOrNull(value);
    return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function extractGameSkillsFromRow(row: Record<string, unknown>) {
    return normalizeGameSkillsOrNull({
        skill_listening_speaking: row.skill_listening_speaking,
        skill_lang_nature: row.skill_lang_nature,
        skill_reading: row.skill_reading,
        skill_writing: row.skill_writing,
    });
}

function createEmptyAnagramItem(id: number, no: number): AnagramItem {
    return {
        id,
        no,
        word: "",
        swapWord: [],
        imageWordFileName: "",
        imageWordFile: null,
        imageWordUrl: null,
        audioWordFileName: "",
        audioWordFile: null,
        audioWordUrl: null,
        imageSwapFileName: "",
        imageSwapFile: null,
        imageSwapUrl: null,
        audioSwapFileName: "",
        audioSwapFile: null,
        audioSwapUrl: null,
        hasHint: false,
        hintText: "",
        hintImageFileName: "",
        hintImageFile: null,
        hintImageUrl: null,
        hintAudioFileName: "",
        hintAudioFile: null,
        hintAudioUrl: null,
    };
}

export default function FindTheAnagram({ createdGameData, initialGameDetail, onCompleted }: FindTheAnagramProps) {
    const nextAnagramIdRef = useRef(6);
    const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
    const initialSuggestionRef = useRef("");
    const initialDescriptionRef = useRef("");
    const initialAnagramIdsRef = useRef<Set<number>>(new Set());
    const initialGameSkillsRef = useRef<GameSkills>({ ...DEFAULT_GAME_SKILLS });

    const [exerciseName, setExerciseName] = useState(createdGameData?.exercise_name ?? "");
    const [suggestion, setSuggestion] = useState("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [isUpdateMode, setIsUpdateMode] = useState(Boolean(initialGameDetail?.game_info?.id));
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
    const [gameSkills, setGameSkills] = useState<GameSkills>({ ...DEFAULT_GAME_SKILLS });
    const [items, setItems] = useState<AnagramItem[]>([
        createEmptyAnagramItem(1, 1),
        createEmptyAnagramItem(2, 2),
        // createEmptyAnagramItem(3, 3),
        // createEmptyAnagramItem(4, 4),
        // createEmptyAnagramItem(5, 5),
    ]);

    const previewUuid = String(
        initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? ""
    ).trim();
    const canPreview = isUpdateMode && Boolean(previewUuid);

    useEffect(() => {
        setExerciseName(createdGameData?.exercise_name ?? "");
        initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
    }, [createdGameData?.exercise_name]);

    useEffect(() => {
        const gameInfoId = (initialGameDetail?.game_info?.id ?? initialGameDetail?.id_game_info) ?? null;
        if (gameInfoId) {
            return;
        }

        const incomingDescription = String(createdGameData?.description ?? "");
        setDescription(incomingDescription);
        initialDescriptionRef.current = incomingDescription;
    }, [createdGameData?.description, initialGameDetail?.game_info?.id, initialGameDetail?.id_game_info]);

    const applyLoadedGameDetail = (detail: FindTheAnagramProps["initialGameDetail"]) => {
        const detailRecord = (detail ?? {}) as Record<string, unknown>;
        const gameInfo = ((detailRecord.game_info as Record<string, unknown> | undefined) ?? {}) as {
            id?: number | string;
            uuid?: string;
            exercise_name?: string;
            suggestion?: string | null;
            description?: string | null;
            skill_listening_speaking?: boolean | "true" | "false" | null;
            skill_lang_nature?: boolean | "true" | "false" | null;
            skill_reading?: boolean | "true" | "false" | null;
            skill_writing?: boolean | "true" | "false" | null;
        };
        const incomingRows = Array.isArray(detailRecord.questions)
            ? (detailRecord.questions as unknown[])
            : Array.isArray(detailRecord.anagrams)
                ? (detailRecord.anagrams as unknown[])
                : [];
        const resolvedGameId = gameInfo.id ?? detailRecord.id_game_info;

        if (!resolvedGameId && incomingRows.length === 0) {
            initialAnagramIdsRef.current = new Set();
            initialGameSkillsRef.current = { ...DEFAULT_GAME_SKILLS };
            setGameSkills({ ...DEFAULT_GAME_SKILLS });
            setIsUpdateMode(false);
            return;
        }

        const incomingExerciseName = String(gameInfo.exercise_name ?? "");
        const incomingSuggestion = String(gameInfo.suggestion ?? "");
        const incomingDescription = String(gameInfo.description ?? "");

        setExerciseName(incomingExerciseName);
        setSuggestion(incomingSuggestion);
        setDescription(incomingDescription);
        initialExerciseNameRef.current = incomingExerciseName;
        initialSuggestionRef.current = incomingSuggestion;
        initialDescriptionRef.current = incomingDescription;

        const mappedItems = incomingRows.length > 0
            ? incomingRows.map((row, index) => {
                const record = row as Record<string, unknown>;
                const mappedId = Number(record.id ?? index + 1);
                const normalizedId = Number.isInteger(mappedId) && mappedId > 0 ? mappedId : index + 1;
                const wordRaw = String(record.word ?? record.question ?? "");
                const swapWordRaw: string[] = Array.isArray(record.swap_word)
                    ? record.swap_word
                    : buildSwapWord(String(record.swap_word ?? record.answer ?? wordRaw));
                const hintRaw = String(record.hint ?? "");
                const imageHintRaw = String(record.image_hint ?? "");
                const soundHintRaw = String(record.sound_hint ?? "");
                return {
                    id: normalizedId,
                    no: Number(record.no ?? index + 1),
                    word: wordRaw,
                    swapWord: swapWordRaw,
                    imageWordFileName: getFileNameFromPath(String(record.image_word ?? "")),
                    imageWordFile: null,
                    imageWordUrl: buildUploadedMediaUrl(String(record.image_word ?? "")),
                    audioWordFileName: getFileNameFromPath(String(record.sound_word ?? "")),
                    audioWordFile: null,
                    audioWordUrl: buildUploadedMediaUrl(String(record.sound_word ?? "")),
                    imageSwapFileName: getFileNameFromPath(String(record.image_swap ?? "")),
                    imageSwapFile: null,
                    imageSwapUrl: buildUploadedMediaUrl(String(record.image_swap ?? "")),
                    audioSwapFileName: getFileNameFromPath(String(record.sound_swap ?? "")),
                    audioSwapFile: null,
                    audioSwapUrl: buildUploadedMediaUrl(String(record.sound_swap ?? "")),
                    hasHint: Boolean(hintRaw.trim() || imageHintRaw || soundHintRaw),
                    hintText: hintRaw,
                    hintImageFileName: getFileNameFromPath(imageHintRaw),
                    hintImageFile: null,
                    hintImageUrl: buildUploadedMediaUrl(imageHintRaw),
                    hintAudioFileName: getFileNameFromPath(soundHintRaw),
                    hintAudioFile: null,
                    hintAudioUrl: buildUploadedMediaUrl(soundHintRaw),
                };
            })
            : [
                createEmptyAnagramItem(1, 1),
                createEmptyAnagramItem(2, 2),
            ];

        const skillsFromGameInfo = extractGameSkillsFromUnknown({
            skill_listening_speaking: gameInfo.skill_listening_speaking,
            skill_lang_nature: gameInfo.skill_lang_nature,
            skill_reading: gameInfo.skill_reading,
            skill_writing: gameInfo.skill_writing,
        });

        let resolvedSkills = skillsFromGameInfo;
        if (incomingRows.length > 0) {
            const firstRow = incomingRows[0] as Record<string, unknown>;
            const firstRowSkills = extractGameSkillsFromRow(firstRow);
            resolvedSkills = firstRowSkills ?? { ...DEFAULT_GAME_SKILLS };

            if (!firstRowSkills) {
                console.warn("[Anagram] Invalid skill values in first row, fallback to default false.");
            }

            for (let index = 1; index < incomingRows.length; index += 1) {
                const rowRecord = incomingRows[index] as Record<string, unknown>;
                const rowSkills = extractGameSkillsFromRow(rowRecord);

                if (!rowSkills) {
                    console.warn(`[Anagram] Invalid skill values in row ${index + 1}, using first row skill values.`);
                    continue;
                }

                if (!areGameSkillsEqual(rowSkills, resolvedSkills)) {
                    console.warn("[Anagram] Skill values are inconsistent across rows. Using the first row values.");
                    break;
                }
            }
        }

        setGameSkills(resolvedSkills);
        initialGameSkillsRef.current = resolvedSkills;

        const normalizedItems = mappedItems.map((item, index) => ({ ...item, no: index + 1 }));
        setItems(normalizedItems);

        initialAnagramIdsRef.current = new Set(normalizedItems.map((item) => item.id));
        nextAnagramIdRef.current = normalizedItems.reduce((max, item) => Math.max(max, item.id), 0) + 1;
        setIsUpdateMode(true);
    };

    useEffect(() => {
        applyLoadedGameDetail(initialGameDetail);
    }, [initialGameDetail]);

    const setNoByOrder = (sourceItems: AnagramItem[]) =>
        sourceItems.map((item, index) => ({ ...item, no: index + 1 }));

    const updateGameSkill = (skillKey: GameSkillKey, checked: boolean) => {
        setGameSkills((previous) => ({
            ...previous,
            [skillKey]: checked,
        }));
    };

    const addItem = () => {
        const newId = nextAnagramIdRef.current;
        nextAnagramIdRef.current += 1;
        setItems((previous) => setNoByOrder([...previous, createEmptyAnagramItem(newId, previous.length + 1)]));
    };

    const removeItem = (id: number) => {
        setItems((previous) => setNoByOrder(previous.filter((item) => item.id !== id)));
    };

    const moveItem = (id: number, direction: -1 | 1) => {
        setItems((previous) => {
            const currentIndex = previous.findIndex((item) => item.id === id);
            if (currentIndex < 0) {
                return previous;
            }

            const nextIndex = currentIndex + direction;
            if (nextIndex < 0 || nextIndex >= previous.length) {
                return previous;
            }

            const reordered = [...previous];
            const [moved] = reordered.splice(currentIndex, 1);
            reordered.splice(nextIndex, 0, moved);
            return setNoByOrder(reordered);
        });
    };

    const updateItem = (id: number, updater: (item: AnagramItem) => AnagramItem) => {
        setItems((previous) =>
            previous.map((item) => (item.id === id ? updater(item) : item))
        );
    };

    const updateTextField = (id: number, field: "word", value: string) => {
        updateItem(id, (item) => ({
            ...item,
            [field]: buildWord(value),
            swapWord: buildSwapWord(value),
        }));
    };

    const setFileOnItem = (
        id: number,
        options: {
            file: File;
            fileNameField:
            | "imageWordFileName"
            | "audioWordFileName"
            | "imageSwapFileName"
            | "audioSwapFileName"
            | "hintImageFileName"
            | "hintAudioFileName";
            fileField:
            | "imageWordFile"
            | "audioWordFile"
            | "imageSwapFile"
            | "audioSwapFile"
            | "hintImageFile"
            | "hintAudioFile";
            urlField:
            | "imageWordUrl"
            | "audioWordUrl"
            | "imageSwapUrl"
            | "audioSwapUrl"
            | "hintImageUrl"
            | "hintAudioUrl";
            forceEnableHint?: boolean;
        }
    ) => {
        updateItem(id, (item) => ({
            ...item,
            [options.fileNameField]: options.file.name,
            [options.fileField]: options.file,
            [options.urlField]: null,
            ...(options.forceEnableHint ? { hasHint: true } : {}),
        }));
    };

    const enableHint = (id: number) => {
        updateItem(id, (item) => ({ ...item, hasHint: true }));
    };

    const clearHint = (id: number) => {
        updateItem(id, (item) => ({
            ...item,
            hasHint: false,
            hintText: "",
            hintImageFileName: "",
            hintImageFile: null,
            hintImageUrl: null,
            hintAudioFileName: "",
            hintAudioFile: null,
            hintAudioUrl: null,
        }));
    };

    const collectUploadTargets = (sourceItems: AnagramItem[]) => {
        const targets: UploadTarget[] = [];

        sourceItems.forEach((item, index) => {
            const timestamp = Date.now();

            if (item.imageWordFile && !item.imageWordUrl) {
                const ext = getFileExtension(item.imageWordFile.name);
                targets.push({
                    key: `a-${index}-image_word`,
                    file: item.imageWordFile,
                    fileName: `image_word_a${item.id}_${timestamp}${ext}`,
                    sectionName: "image_word",
                    refId: `anagram_${item.id}`,
                });
            }

            if (item.audioWordFile && !item.audioWordUrl) {
                const ext = getFileExtension(item.audioWordFile.name);
                targets.push({
                    key: `a-${index}-sound_word`,
                    file: item.audioWordFile,
                    fileName: `sound_word_a${item.id}_${timestamp}${ext}`,
                    sectionName: "sound_word",
                    refId: `anagram_${item.id}`,
                });
            }

            if (item.imageSwapFile && !item.imageSwapUrl) {
                const ext = getFileExtension(item.imageSwapFile.name);
                targets.push({
                    key: `a-${index}-image_swap_word`,
                    file: item.imageSwapFile,
                    fileName: `image_swap_word_a${item.id}_${timestamp}${ext}`,
                    sectionName: "image_swap_word",
                    refId: `anagram_${item.id}`,
                });
            }

            if (item.audioSwapFile && !item.audioSwapUrl) {
                const ext = getFileExtension(item.audioSwapFile.name);
                targets.push({
                    key: `a-${index}-sound_swap_word`,
                    file: item.audioSwapFile,
                    fileName: `sound_swap_word_a${item.id}_${timestamp}${ext}`,
                    sectionName: "sound_swap_word",
                    refId: `anagram_${item.id}`,
                });
            }

            if (item.hintImageFile && !item.hintImageUrl) {
                const ext = getFileExtension(item.hintImageFile.name);
                targets.push({
                    key: `a-${index}-image_hint`,
                    file: item.hintImageFile,
                    fileName: `image_hint_a${item.id}_${timestamp}${ext}`,
                    sectionName: "image_hint",
                    refId: `anagram_${item.id}`,
                });
            }

            if (item.hintAudioFile && !item.hintAudioUrl) {
                const ext = getFileExtension(item.hintAudioFile.name);
                targets.push({
                    key: `a-${index}-sound_hint`,
                    file: item.hintAudioFile,
                    fileName: `sound_hint_a${item.id}_${timestamp}${ext}`,
                    sectionName: "sound_hint",
                    refId: `anagram_${item.id}`,
                });
            }
        });

        return targets;
    };

    const uploadPendingMedia = async (targets: UploadTarget[]) => {
        const uploadedUrlByKey = new Map<string, string>();

        if (targets.length === 0) {
            return uploadedUrlByKey;
        }

        const uploadResults = await Promise.allSettled(
            targets.map((target) =>
                uploadAPI
                    .uploadMedia({
                        file: target.file,
                        fileName: target.fileName,
                        sectionName: target.sectionName,
                        ref_id: target.refId,
                    })
                    .then((response) => ({ target, response }))
            )
        );

        for (const result of uploadResults) {
            if (result.status === "rejected") {
                throw new Error("upload_failed");
            }

            const response = result.value.response as { status?: string; url?: string };
            if (response?.status === "failed" || !response?.url) {
                throw new Error("upload_failed");
            }

            uploadedUrlByKey.set(result.value.target.key, response.url);
        }

        return uploadedUrlByKey;
    };

    const applyUploadedUrlsToState = (sourceItems: AnagramItem[], uploadedUrlByKey: Map<string, string>) => {
        return sourceItems.map((item, index) => ({
            ...item,
            imageWordUrl: uploadedUrlByKey.get(`a-${index}-image_word`) ?? item.imageWordUrl,
            imageWordFile: uploadedUrlByKey.get(`a-${index}-image_word`) ? null : item.imageWordFile,
            audioWordUrl: uploadedUrlByKey.get(`a-${index}-sound_word`) ?? item.audioWordUrl,
            audioWordFile: uploadedUrlByKey.get(`a-${index}-sound_word`) ? null : item.audioWordFile,
            imageSwapUrl: uploadedUrlByKey.get(`a-${index}-image_swap_word`) ?? item.imageSwapUrl,
            imageSwapFile: uploadedUrlByKey.get(`a-${index}-image_swap_word`) ? null : item.imageSwapFile,
            audioSwapUrl: uploadedUrlByKey.get(`a-${index}-sound_swap_word`) ?? item.audioSwapUrl,
            audioSwapFile: uploadedUrlByKey.get(`a-${index}-sound_swap_word`) ? null : item.audioSwapFile,
            hintImageUrl: uploadedUrlByKey.get(`a-${index}-image_hint`) ?? item.hintImageUrl,
            hintImageFile: uploadedUrlByKey.get(`a-${index}-image_hint`) ? null : item.hintImageFile,
            hintAudioUrl: uploadedUrlByKey.get(`a-${index}-sound_hint`) ?? item.hintAudioUrl,
            hintAudioFile: uploadedUrlByKey.get(`a-${index}-sound_hint`) ? null : item.hintAudioFile,
        }));
    };

    const refreshFromServer = async () => {
        if (!previewUuid) {
            return;
        }

        const latestGameDetail = await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
        if (latestGameDetail && (latestGameDetail as { status?: string }).status !== "failed") {
            applyLoadedGameDetail(latestGameDetail as FindTheAnagramProps["initialGameDetail"]);
        }
    };

    const handleSubmitAnagram = async () => {
        if (!createdGameData?.id) {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "ไม่พบข้อมูลเกมที่สร้างล่าสุด",
                type: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const trimmedExerciseName = exerciseName.trim();
            const trimmedSuggestion = suggestion.trim();
            const trimmedDescription = description.trim();
            const shouldUpdateExerciseName =
                trimmedExerciseName !== initialExerciseNameRef.current.trim();
            const shouldUpdateSuggestion =
                trimmedSuggestion !== initialSuggestionRef.current.trim();
            const shouldUpdateDescription =
                trimmedDescription !== initialDescriptionRef.current.trim();

            if (shouldUpdateExerciseName || shouldUpdateSuggestion || shouldUpdateDescription) {
                const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
                    gameId: createdGameData.id,
                    ...(shouldUpdateExerciseName ? { exercise_name: trimmedExerciseName } : {}),
                    ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
                    ...(shouldUpdateDescription ? { description: trimmedDescription } : {}),
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "อัปเดตชื่อกิจกรรม คำแนะนำ หรือรายละเอียดไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                initialExerciseNameRef.current = trimmedExerciseName;
                initialSuggestionRef.current = trimmedSuggestion;
                initialDescriptionRef.current = trimmedDescription;
                setExerciseName(trimmedExerciseName);
                setSuggestion(trimmedSuggestion);
                setDescription(trimmedDescription);
            }

            const uploadTargets = collectUploadTargets(items);
            const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
            const itemsWithResolvedUrls = applyUploadedUrlsToState(items, uploadedUrlByKey);

            if (!Array.isArray(itemsWithResolvedUrls) || itemsWithResolvedUrls.length === 0) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ต้องมีรายการ Anagram อย่างน้อย 1 รายการ",
                    type: "error",
                });
                return;
            }

            const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
            if (!normalizedGameSkills) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ข้อมูลสมรรถนะไม่ถูกต้อง (ต้องเป็น true/false)",
                    type: "error",
                });
                return;
            }

            const seenNos = new Set<number>();
            for (let index = 0; index < itemsWithResolvedUrls.length; index += 1) {
                const item = itemsWithResolvedUrls[index];
                const no = index + 1;

                if (!Number.isInteger(no)) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "ลำดับรายการไม่ถูกต้อง",
                        type: "error",
                    });
                    return;
                }

                if (seenNos.has(no)) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "ลำดับรายการซ้ำในคำขอ",
                        type: "error",
                    });
                    return;
                }
                seenNos.add(no);

                const normalizedWord = buildWord(item.word);
                if (!normalizedWord) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `คำลำดับ ${no} ต้องไม่ว่าง`,
                        type: "error",
                    });
                    return;
                }

                const normalizedSwapWord = item.swapWord.length > 0 ? item.swapWord : buildSwapWord(item.word);
                if (!Array.isArray(normalizedSwapWord)) {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `ข้อมูลสลับคำของลำดับ ${no} ต้องเป็น array`,
                        type: "error",
                    });
                    return;
                }
            }

            const payloadRows = itemsWithResolvedUrls.map((item, index) => ({
                no: index + 1,
                word: buildWord(item.word),
                swap_word: normalizeSwapWord(item.swapWord, item.word),
                hint: item.hasHint ? item.hintText.trim() || null : null,
                image_word: item.imageWordUrl || null,
                sound_word: item.audioWordUrl || null,
                image_swap_word: item.imageSwapUrl || null,
                sound_swap_word: item.audioSwapUrl || null,
                image_hint: item.hasHint ? item.hintImageUrl || null : null,
                sound_hint: item.hasHint ? item.hintAudioUrl || null : null,
            }));

            const response = await anagramAPI.createAnagram({
                gameId: createdGameData.id,
                skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
                skill_lang_nature: normalizedGameSkills.skill_lang_nature,
                skill_reading: normalizedGameSkills.skill_reading,
                skill_writing: normalizedGameSkills.skill_writing,
                anagrams: payloadRows,
            });

            if (response?.status === "failed") {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "บันทึก Anagram ไม่สำเร็จ",
                    type: "error",
                });
                return;
            }

            setItems(setNoByOrder(itemsWithResolvedUrls));
            initialGameSkillsRef.current = normalizedGameSkills;
            await refreshFromServer();
            setIsUpdateMode(true);
            setStatusPopUp({
                title: "สำเร็จ",
                message: "บันทึก Anagram เรียบร้อยแล้ว",
                type: "success",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateAnagram = async () => {
        if (!createdGameData?.id) {
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "ไม่พบข้อมูลเกมที่ต้องการอัปเดต",
                type: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const trimmedExerciseName = exerciseName.trim();
            const trimmedSuggestion = suggestion.trim();
            const trimmedDescription = description.trim();
            const shouldUpdateExerciseName =
                trimmedExerciseName !== initialExerciseNameRef.current.trim();
            const shouldUpdateSuggestion =
                trimmedSuggestion !== initialSuggestionRef.current.trim();
            const shouldUpdateDescription =
                trimmedDescription !== initialDescriptionRef.current.trim();

            if (shouldUpdateExerciseName || shouldUpdateSuggestion || shouldUpdateDescription) {
                const updateResponse = await mainAPI.updateExerciseNameAndSuggestion({
                    gameId: createdGameData.id,
                    ...(shouldUpdateExerciseName ? { exercise_name: trimmedExerciseName } : {}),
                    ...(shouldUpdateSuggestion ? { suggestion: trimmedSuggestion } : {}),
                    ...(shouldUpdateDescription ? { description: trimmedDescription } : {}),
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "อัปเดตชื่อกิจกรรม คำแนะนำ หรือรายละเอียดไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }

                initialExerciseNameRef.current = trimmedExerciseName;
                initialSuggestionRef.current = trimmedSuggestion;
                initialDescriptionRef.current = trimmedDescription;
                setExerciseName(trimmedExerciseName);
                setSuggestion(trimmedSuggestion);
                setDescription(trimmedDescription);
            }

            const uploadTargets = collectUploadTargets(items);
            const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
            const itemsWithResolvedUrls = applyUploadedUrlsToState(items, uploadedUrlByKey);

            const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
            if (!normalizedGameSkills) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ข้อมูลสมรรถนะไม่ถูกต้อง (ต้องเป็น true/false)",
                    type: "error",
                });
                return;
            }

            const changedSkillPayload: Partial<GameSkills> = {};
            if (normalizedGameSkills.skill_listening_speaking !== initialGameSkillsRef.current.skill_listening_speaking) {
                changedSkillPayload.skill_listening_speaking = normalizedGameSkills.skill_listening_speaking;
            }
            if (normalizedGameSkills.skill_lang_nature !== initialGameSkillsRef.current.skill_lang_nature) {
                changedSkillPayload.skill_lang_nature = normalizedGameSkills.skill_lang_nature;
            }
            if (normalizedGameSkills.skill_reading !== initialGameSkillsRef.current.skill_reading) {
                changedSkillPayload.skill_reading = normalizedGameSkills.skill_reading;
            }
            if (normalizedGameSkills.skill_writing !== initialGameSkillsRef.current.skill_writing) {
                changedSkillPayload.skill_writing = normalizedGameSkills.skill_writing;
            }

            const currentIds = new Set<number>(itemsWithResolvedUrls.map((item) => item.id));
            const deletedIds = Array.from(initialAnagramIdsRef.current).filter((id) => !currentIds.has(id));

            for (const deletedId of deletedIds) {
                const deleteResponse = await anagramAPI.hardDeleteAnagram({ anagramId: deletedId });
                if (deleteResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `ลบรายการ id ${deletedId} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }
            }

            const existingRows = itemsWithResolvedUrls
                .filter((item) => initialAnagramIdsRef.current.has(item.id))
                .map((item) => ({ ...item, swapWord: normalizeSwapWord(item.swapWord, item.word) }));
            const newRows = itemsWithResolvedUrls
                .filter((item) => !initialAnagramIdsRef.current.has(item.id))
                .map((item) => ({ ...item, swapWord: normalizeSwapWord(item.swapWord, item.word) }));

            for (const row of existingRows) {
                const updateResponse = await anagramAPI.updateAnagram({
                    anagramId: row.id,
                    no: row.no,
                    word: buildWord(row.word),
                    swap_word: normalizeSwapWord(row.swapWord, row.word),
                    hint: row.hasHint ? row.hintText.trim() || null : null,
                    image_word: row.imageWordUrl || null,
                    sound_word: row.audioWordUrl || null,
                    image_swap_word: row.imageSwapUrl || null,
                    sound_swap_word: row.audioSwapUrl || null,
                    image_hint: row.hasHint ? row.hintImageUrl || null : null,
                    sound_hint: row.hasHint ? row.hintAudioUrl || null : null,
                    ...changedSkillPayload,
                });

                if (updateResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: `อัปเดตรายการลำดับ ${row.no} ไม่สำเร็จ`,
                        type: "error",
                    });
                    return;
                }
            }

            if (newRows.length > 0) {
                const createNewRowsResponse = await anagramAPI.createAnagram({
                    gameId: createdGameData.id,
                    skill_listening_speaking: normalizedGameSkills.skill_listening_speaking,
                    skill_lang_nature: normalizedGameSkills.skill_lang_nature,
                    skill_reading: normalizedGameSkills.skill_reading,
                    skill_writing: normalizedGameSkills.skill_writing,
                    anagrams: newRows.map((row) => ({
                        no: row.no,
                        word: buildWord(row.word),
                        swap_word: normalizeSwapWord(row.swapWord, row.word),
                        hint: row.hasHint ? row.hintText.trim() || null : null,
                        image_word: row.imageWordUrl || null,
                        sound_word: row.audioWordUrl || null,
                        image_swap_word: row.imageSwapUrl || null,
                        sound_swap_word: row.audioSwapUrl || null,
                        image_hint: row.hasHint ? row.hintImageUrl || null : null,
                        sound_hint: row.hasHint ? row.hintAudioUrl || null : null,
                    })),
                });

                if (createNewRowsResponse?.status === "failed") {
                    setStatusPopUp({
                        title: "ไม่สำเร็จ",
                        message: "เพิ่มรายการใหม่ไม่สำเร็จ",
                        type: "error",
                    });
                    return;
                }
            }

            setItems(setNoByOrder(itemsWithResolvedUrls));
            await refreshFromServer();

            initialAnagramIdsRef.current = new Set(itemsWithResolvedUrls.map((item) => item.id));
            initialGameSkillsRef.current = normalizedGameSkills;
            setIsUpdateMode(true);
            setStatusPopUp({
                title: "สำเร็จ",
                message: "อัปเดต Anagram เรียบร้อยแล้ว",
                type: "success",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseStatusPopUp = () => {
        setStatusPopUp(null);
    };

    const handleClickFinishButton = () => {
        if (isSubmitting) {
            return;
        }
        setIsConfirmSaveOpen(true);
    };

    const handleCancelConfirmSave = () => {
        setIsConfirmSaveOpen(false);
    };

    const handleConfirmSave = () => {
        setIsConfirmSaveOpen(false);
        if (isUpdateMode) {
            void handleUpdateAnagram();
            return;
        }
        void handleSubmitAnagram();
    };

    const handleOpenPreview = () => {
        if (!canPreview) {
            return;
        }

        if (!previewUuid) {
            setStatusPopUp({
                title: "ไม่สามารถ Preview ได้",
                message: "ไม่พบ UUID ของเกมสำหรับการ Preview",
                type: "error",
            });
            return;
        }

        const previewBaseUrl = "https://thaipilagame.gforcesolution.com";
        const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
        const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}`;
        setPreviewIframeUrl(previewUrl);
    };

    const handleClosePreview = () => {
        setPreviewIframeUrl(null);
    };

    const onFileInputChange = (
        event: ChangeEvent<HTMLInputElement>,
        callback: (file: File) => void
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            callback(file);
        }
        event.currentTarget.value = "";
    };

    return (
        <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
            <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
                <span className="font-semibold text-[#4E4E4E]">แก้ไขเนื้อหา &gt; </span>
                <span className="font-bold text-[#2b2b2b]">Anagram</span>
            </div>

            <div className="mt-4 space-y-3">
                <div>
                    <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">ชื่อกิจกรรม</label>
                    <input
                        value={exerciseName}
                        onChange={(event) => setExerciseName(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="ไม่มีชื่อกิจกรรม"
                    />
                </div>

                <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        คำแนะนำในการเล่น
                        <FiInfo size={14} />
                    </label>
                    <input
                        value={suggestion}
                        onChange={(event) => setSuggestion(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="ลากและสลับคำให้ถูกต้อง"
                    />
                </div>

                <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        Description / รายละเอียด
                    </label>
                    <input
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        placeholder="เลือกคำตอบที่ถูกต้อง"
                    />
                </div>

            </div>

            <div className="mt-5 rounded-md border border-[#e0e0e0] bg-[#f9f9f9] p-3">
                <div className="grid grid-cols-[40px_1fr_60px] items-center gap-4 pb-2">
                    <span className="text-lg font-medium text-[#5a5a5a] text-center">ลำดับ</span>
                    {/* <span className="inline-block rounded-md bg-[#fcee54] px-10 py-1.5 text-lg font-semibold text-[#333]">คำ</span>
                    <span className="text-lg font-medium text-[#5a5a5a] text-center">จัดการ</span> */}
                </div>

                <div className="space-y-3">
                    {items.map((item, index) => {
                        const wordImageId = `word-image-${item.id}`;
                        const wordAudioId = `word-audio-${item.id}`;
                        const hintImageId = `hint-image-${item.id}`;
                        const hintAudioId = `hint-audio-${item.id}`;

                        return (
                            <div key={item.id} className="flex flex-col">
                                <div className="grid grid-cols-[40px_1fr_60px] items-center gap-4">
                                    <span className="text-lg font-semibold text-[#5a5a5a] text-center">{index + 1}.</span>

                                    <div className="relative">
                                        <div className="flex h-9.5 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                            <input
                                                value={item.word}
                                                onChange={(event) => updateTextField(item.id, "word", event.target.value)}
                                                className="h-full w-full min-w-0 bg-transparent text-lg outline-none"
                                            />
                                            <div className="flex items-center gap-3 pl-2">
                                                <label htmlFor={wordImageId} className="cursor-pointer text-[#4ca2e2] hover:text-blue-500">
                                                    <LuImage size={18} />
                                                </label>
                                                <input
                                                    id={wordImageId}
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(event) => onFileInputChange(event, (file) =>
                                                        setFileOnItem(item.id, {
                                                            file,
                                                            fileNameField: "imageWordFileName",
                                                            fileField: "imageWordFile",
                                                            urlField: "imageWordUrl",
                                                        })
                                                    )}
                                                />

                                                <label htmlFor={wordAudioId} className="cursor-pointer text-[#626262] hover:text-black">
                                                    <FiVolume2 size={18} />
                                                </label>
                                                <input
                                                    id={wordAudioId}
                                                    type="file"
                                                    accept="audio/*"
                                                    className="hidden"
                                                    onChange={(event) => onFileInputChange(event, (file) =>
                                                        setFileOnItem(item.id, {
                                                            file,
                                                            fileNameField: "audioWordFileName",
                                                            fileField: "audioWordFile",
                                                            urlField: "audioWordUrl",
                                                        })
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        {item.imageWordFileName || item.audioWordFileName ? (
                                            <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-[#6b6b6b] absolute -bottom-4 left-0">
                                                {item.imageWordFileName ? <span>รูป: {item.imageWordFileName}</span> : null}
                                                {item.audioWordFileName ? <span>เสียง: {item.audioWordFileName}</span> : null}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="flex items-center justify-center gap-3">
                                        <div className="flex items-center text-[#555]">
                                            <button
                                                type="button"
                                                onClick={() => moveItem(item.id, -1)}
                                                disabled={index === 0}
                                                className="cursor-pointer hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                                                title="เลื่อนขึ้น"
                                            >
                                                ↑
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveItem(item.id, 1)}
                                                disabled={index === items.length - 1}
                                                className="ml-2 cursor-pointer hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                                                title="เลื่อนลง"
                                            >
                                                ↓
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeItem(item.id)}
                                            className="cursor-pointer text-[#ff8e8e] hover:text-red-500"
                                            title="ลบ"
                                        >
                                            <MdDeleteOutline size={22} />
                                        </button>
                                    </div>
                                </div>

                                {/* {item.hasHint ? (
                                    <div className="mt-2 ml-12 px-2 py-2">
                                        <div className="mb-1 flex items-center justify-between">
                                            <span className="text-lg font-medium text-[#5b5b5b]">คำใบ้</span>
                                        </div>
                                        <div className="relative">
                                            <div className="flex h-9.5 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                                <input
                                                    value={item.hintText}
                                                    onChange={(event) => updateItem(item.id, (row) => ({ ...row, hasHint: true, hintText: event.target.value }))}
                                                    className="h-8 w-full min-w-0 text-lg outline-none"
                                                    placeholder="พิมพ์คำใบ้ (ไม่บังคับ)"
                                                />
                                                <div className="flex items-center gap-3 pl-2">
                                                    <label htmlFor={hintImageId} className="cursor-pointer text-[#62aee0]">
                                                        <LuImage size={18} />
                                                    </label>
                                                    <input
                                                        id={hintImageId}
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={(event) => onFileInputChange(event, (file) =>
                                                            setFileOnItem(item.id, {
                                                                file,
                                                                fileNameField: "hintImageFileName",
                                                                fileField: "hintImageFile",
                                                                urlField: "hintImageUrl",
                                                                forceEnableHint: true,
                                                            })
                                                        )}
                                                    />

                                                    <label htmlFor={hintAudioId} className="cursor-pointer text-[#4b4b4b]">
                                                        <FiVolume2 size={18} />
                                                    </label>
                                                    <input
                                                        id={hintAudioId}
                                                        type="file"
                                                        accept="audio/*"
                                                        className="hidden"
                                                        onChange={(event) => onFileInputChange(event, (file) =>
                                                            setFileOnItem(item.id, {
                                                                file,
                                                                fileNameField: "hintAudioFileName",
                                                                fileField: "hintAudioFile",
                                                                urlField: "hintAudioUrl",
                                                                forceEnableHint: true,
                                                            })
                                                        )}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => clearHint(item.id)}
                                                        className="cursor-pointer justify-self-center text-[#f18d8d] hover:text-red-500"
                                                    >
                                                        <MdDeleteOutline size={22} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        {item.hintImageFileName || item.hintAudioFileName ? (
                                            <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-[#6b6b6b]">
                                                {item.hintImageFileName ? <span>รูปคำใบ้: {item.hintImageFileName}</span> : null}
                                                {item.hintAudioFileName ? <span>เสียงคำใบ้: {item.hintAudioFileName}</span> : null}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => enableHint(item.id)}
                                        className="mt-2 text-start ml-14 cursor-pointer text-sm font-medium text-[#4d6ea9] hover:text-[#2f4d83]"
                                    >
                                        ต้องการเพิ่มคำใบ้หรือไม่?
                                    </button>
                                )} */}
                            </div>
                        );
                    })}
                </div>

                <div className="mt-5 ml-3">
                    <button
                        type="button"
                        onClick={addItem}
                        className="ml-10 rounded border border-[#c2c2c2] bg-[#e6e6e6] px-4 py-1.5 text-lg font-medium text-[#444] hover:bg-[#d6d6d6] cursor-pointer"
                    >
                        + เพิ่มรายการ
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-2 p-5 ml-10">
                <label className="text-sm font-bold text-[#555]">สมรรถนะ:</label>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gameSkills.skill_listening_speaking}
                            onChange={(event) => updateGameSkill("skill_listening_speaking", event.target.checked)}
                            className="h-4 w-4 rounded border-[#ccc]"
                        />
                        การฟัง การดู และการพูดเพื่อพัฒนาการคิด
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gameSkills.skill_reading}
                            onChange={(event) => updateGameSkill("skill_reading", event.target.checked)}
                            className="h-4 w-4 rounded border-[#ccc]"
                        />
                        การอ่านเพื่อพัฒนาการคิด
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={gameSkills.skill_lang_nature}
                            onChange={(event) => updateGameSkill("skill_lang_nature", event.target.checked)}
                            className="h-4 w-4 rounded border-[#ccc]"
                        />
                        การเข้าใจธรรมชาติของภาษาไทยและการใช้ภาษาไทย
                    </label>
                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm font-medium text-[#444] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={gameSkills.skill_writing}
                                onChange={(event) => updateGameSkill("skill_writing", event.target.checked)}
                                className="h-4 w-4 rounded border-[#ccc]"
                            />
                            การเขียนเพื่อพัฒนาการคิด
                        </label>
                    </div>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-3">
                <button
                    type="button"
                    onClick={handleOpenPreview}
                    disabled={!canPreview}
                    className="cursor-pointer rounded bg-[#4E4E4E] px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#3d3d3d] disabled:cursor-not-allowed disabled:bg-[#9a9a9a] disabled:shadow-none"
                >
                    Preview
                </button>
                <button
                    type="button"
                    onClick={handleClickFinishButton}
                    disabled={isSubmitting}
                    className={`cursor-pointer rounded px-7 py-2 text-lg font-semibold text-white shadow-[0_2px_0_rgba(0,0,0,0.2)] disabled:cursor-not-allowed disabled:opacity-70 ${isUpdateMode ? "bg-[#1e4fa3] hover:bg-[#183f82]" : "bg-[#f4d735] text-[#4a4a4a] hover:bg-[#e0c91a]"}`}
                >
                    {isSubmitting ? "กำลังบันทึก..." : isUpdateMode ? "อัพเดท" : "บันทึก"}
                </button>
            </div>

            {isConfirmSaveOpen ? (
                <ConfirmPopUp
                    title={isUpdateMode ? "ยืนยันการอัพเดท" : "ยืนยันการบันทึก"}
                    message={isUpdateMode ? "ต้องการอัพเดทข้อมูลหรือไม่" : "ต้องการบันทึกข้อมูลหรือไม่"}
                    cancelText="ยกเลิก"
                    confirmText={isUpdateMode ? "อัพเดท" : "ยืนยัน"}
                    onCancel={handleCancelConfirmSave}
                    onConfirm={handleConfirmSave}
                    isConfirming={isSubmitting}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={handleCloseStatusPopUp}
                />
            ) : null}

            {previewIframeUrl ? (
                <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 p-4">
                    <div className="flex h-[90vh] w-full max-w-400 flex-col overflow-hidden rounded-lg bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center justify-between border-b border-[#e2e2e2] px-4 py-2">
                            <span className="text-base font-semibold text-[#2B2B2B]">Preview</span>
                            <button
                                type="button"
                                onClick={handleClosePreview}
                                className="cursor-pointer rounded bg-[#F54B4B] px-3 py-1 text-sm font-medium text-[#ffffff] hover:bg-[#F62A2A]"
                            >
                                ปิด
                            </button>
                        </div>
                        <iframe
                            src={previewIframeUrl}
                            title="Anagram Preview"
                            className="h-full w-full border-0"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : null}
        </section>
    );
}
